import { JSX, Suspense, lazy } from "react";

import { AsyncDataSuspense } from "@tsp-core/async/components/async-data-suspense";
import { FormProvider } from "@tsp-core/form/components/form-provider";
import { FormMetadata } from "@tsp-core/form/types";

import { useConnectedWithQueryForm } from "@tsp-features/form/hooks/use-connected-with-query-form";

import { accountsApi } from "@tsp-domains/accounts/api";
import { contractorsAccountsApi } from "@tsp-domains/contractor-accounts/api";
import { contractsApi } from "@tsp-domains/contracts/api";
import { INVOICES_RECORDS_LISTING_DEFAULT_FILTER_VALUES } from "@tsp-domains/invoices/constants";
import { systemTypesApi } from "@tsp-domains/system-types/api";

import { Access } from "../../features/auth/components";
import { Layout } from "../../features/layout";
import { UserStateData } from "../../features/user";

const Content = lazy(() => import("./invoices-listing.content"));
const checkAccess = (user: UserStateData) => user.authenticated;

const METADATA: FormMetadata = {
  flow: "filter",
  id: "invoices-listing",
};

export default function InvoicesListingPage(): JSX.Element {
  const loadUserContractsQuery = contractsApi.useLoadUserContractsQuery();
  const loadUserAccountsInfoQuery = accountsApi.useGetUserAccountsInfoQuery();
  const loadContractorsAccountsQuery = contractorsAccountsApi.useGetContractorsAccountsQuery();
  const loadSystemTypesDictionaryQuery = systemTypesApi.useGetSystemTypesDictionaryQuery();

  const form = useConnectedWithQueryForm({ defaultValues: INVOICES_RECORDS_LISTING_DEFAULT_FILTER_VALUES });

  return (
    <Access checkAccess={checkAccess}>
      <Layout loading={false}>
        <AsyncDataSuspense
          queries={[
            loadUserContractsQuery,
            loadUserAccountsInfoQuery,
            loadContractorsAccountsQuery,
            loadSystemTypesDictionaryQuery,
          ]}
        >
          <Suspense fallback={null}>
            <FormProvider metadata={METADATA} {...form}>
              <Content />
            </FormProvider>
          </Suspense>
        </AsyncDataSuspense>
      </Layout>
    </Access>
  );
}
